/* eslint-disable react/jsx-no-bind -- the inline arrows in the pagination props predate this file's TS-era lint gate */
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Pagination from '@mui/material/Pagination'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'
import { startOfDay, endOfDay } from 'date-fns'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import { useLazyGetAccountRouteStopsQuery } from 'api/accounts/accountRouting'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import HHTablePagination from 'components/common/HHTablePagination'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'
import { selectIsInventoryFeatureEnabled } from 'data/tenant/tenantSettingsSelectors'
import { CUSTOMER_DETAILS_DEFAULT_PAGINATION } from 'settings/constants/pagination'
import { setBulkMoveStopsState } from 'slices/route/bulkMoveBetweenRoutesSlice'
import T from 'T'
import { deserializeDate, formatDateToBEFormatDateFns, serializeDate } from 'utils/date'

import ServiceItem from './ServiceItem/ServiceItem'

const { INITIAL_PAGE, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } = CUSTOMER_DETAILS_DEFAULT_PAGINATION
const MAX_SHIMMER_ROWS = 10

const RoutingServiceList = ({ accountId, locationId, serviceDate: controlledServiceDate, filterByServiceName }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { watch } = useFormContext()
  const [pagination, setPagination] = useState({ page: INITIAL_PAGE, rowsPerPage: ROWS_PER_PAGE })
  const [getAccountRouteStops, { data, isSuccess, isLoading, isFetching }] = useLazyGetAccountRouteStopsQuery()
  const stops = get(data, 'stops', [])
  const serializedServiceDate = useSelector(state => get(state, 'CustomerDetails.routing.serializedServiceDate'))
  const reduxServiceDate = useMemo(
    () => (serializedServiceDate ? deserializeDate(serializedServiceDate) : new Date()),
    [serializedServiceDate]
  )
  const serviceDate = controlledServiceDate || reduxServiceDate
  const formattedServiceDate = formatDateToBEFormatDateFns(serviceDate)
  const isInventoryFeatureEnabled = useSelector(selectIsInventoryFeatureEnabled)

  const selectedRoutes = watch('selectedRoutes')
  const routesOptions = watch('routesOptions')
  const selectedServices = watch('selectedServices')
  const servicesOptions = watch('servicesOptions')
  const selectedWorkOrderStatuses = watch('selectedWorkOrderStatuses')
  const selectedDateRange = watch('selectedDateRange')

  const serviceFilteredStops = useMemo(() => {
    if (!filterByServiceName || !stops.length) return stops
    return stops.filter(stop => get(stop, 'serviceName') === filterByServiceName)
  }, [stops, filterByServiceName])

  const filteredStops = useMemo(() => {
    if (!serviceFilteredStops.length) return serviceFilteredStops

    const hasDateRange = selectedDateRange && (selectedDateRange[0] || selectedDateRange[1])
    if (hasDateRange) {
      const rangeStart = selectedDateRange[0] ? startOfDay(selectedDateRange[0]) : null
      const rangeEndDate = selectedDateRange[1] || selectedDateRange[0]
      const rangeEnd = rangeEndDate ? endOfDay(rangeEndDate) : null
      const normalizedServiceDate = startOfDay(serviceDate)

      if (rangeStart && rangeEnd && (normalizedServiceDate < rangeStart || normalizedServiceDate > rangeEnd)) return []
      if (rangeStart && !rangeEnd && normalizedServiceDate < rangeStart) return []
    }

    const isAllRoutesSelected = !selectedRoutes?.length || !routesOptions?.length || selectedRoutes.length === routesOptions.length
    const isAllServicesSelected =
      !selectedServices?.length || !servicesOptions?.length || selectedServices.length === servicesOptions.length
    const isAllStatusesSelected = !selectedWorkOrderStatuses?.length

    if (isAllRoutesSelected && isAllServicesSelected && isAllStatusesSelected) return serviceFilteredStops

    const selectedRouteIds = new Set(selectedRoutes.map(r => r.id))
    const includeUnassigned = selectedRouteIds.has(UNASSIGNED_ROUTE.id)
    const selectedServiceIds = new Set(selectedServices.map(s => s.id))
    const selectedStatusIds = new Set(selectedWorkOrderStatuses.map(s => s.id))

    return serviceFilteredStops.filter(stop => {
      if (!isAllRoutesSelected) {
        const routeId = get(stop, 'routeId')
        const matchesRoute = routeId ? selectedRouteIds.has(routeId) : includeUnassigned
        if (!matchesRoute) return false
      }

      // By id, not name: two priced services can share a name, and the calendar filters by id.
      if (!isAllServicesSelected && !selectedServiceIds.has(get(stop, 'pricedServiceId'))) return false

      if (!isAllStatusesSelected) {
        const workOrderStatus = get(stop, 'workOrderStatus')
        if (!selectedStatusIds.has(workOrderStatus)) return false
      }

      return true
    })
  }, [
    serviceFilteredStops,
    serviceDate,
    selectedRoutes,
    routesOptions,
    selectedServices,
    servicesOptions,
    selectedWorkOrderStatuses,
    selectedDateRange,
  ])

  const filteredStopsCount = filteredStops.length
  const maxShimmerRows = filteredStopsCount > MAX_SHIMMER_ROWS ? MAX_SHIMMER_ROWS : filteredStopsCount
  const shimmersArray = Array.from({ length: maxShimmerRows }, (_, i) => `shimmer-${i}`)
  const { page, rowsPerPage } = pagination
  const currentPageStops = filteredStops.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
  const currentPageStopsLength = currentPageStops.length

  const handlePageChange = (newPage = INITIAL_PAGE) => setPagination(p => ({ ...p, page: newPage }))

  const handleItemClick = useCallback(
    ({ routeId }) => {
      dispatch(
        setBulkMoveStopsState({
          isOpen: true,
          isSelectDateDialogOpen: false,
          serializedServiceDate: serializedServiceDate || serializeDate(new Date()),
          initialMoveFromRouteId: routeId ?? '',
        })
      )
    },
    [dispatch, serializedServiceDate]
  )

  useEffect(() => {
    getAccountRouteStops({
      accountId,
      serviceDate: formattedServiceDate,
      ...(locationId && { locationId }),
    })
  }, [accountId, locationId, formattedServiceDate, getAccountRouteStops])

  useEffect(() => {
    if (currentPageStopsLength === 0 && filteredStopsCount > 0) {
      handlePageChange()
    }
  }, [currentPageStopsLength, filteredStopsCount])

  useEffect(() => {
    handlePageChange()
  }, [locationId, selectedRoutes, selectedServices, selectedWorkOrderStatuses, selectedDateRange])

  const showList = isSuccess && !isFetching && !isLoading

  return (
    <>
      {showList && filteredStopsCount === 0 && <HHSectionPlaceholder title={T.NO_STOPS_FOUND} />}
      <List disablePadding sx={{ pb: 0 }}>
        {showList &&
          currentPageStops.map(service => (
            <ServiceItem
              key={get(service, 'workOrderId')}
              containerCount={get(service, 'containerCount') ?? (isInventoryFeatureEnabled ? 0 : 1)}
              service={service}
              onItemClick={handleItemClick}
            />
          ))}
        {(isFetching || isLoading) &&
          shimmersArray.map(key => (
            <ListItem
              key={key}
              sx={{
                height: '64px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-of-type': {
                  borderBottom: 0,
                },
              }}
            >
              <ListItemAvatar>
                <Skeleton height={24} variant="circular" width={24} />
              </ListItemAvatar>
              <ListItemText primary={<Skeleton width={200} />} secondary={<Skeleton width={100} />} />
            </ListItem>
          ))}
      </List>
      {showList && filteredStopsCount > rowsPerPage && (
        <Box alignItems="center" backgroundColor="background.header" display="flex" justifyContent="space-between" pl={3} pr={2}>
          <HHTablePagination
            ActionsComponent={params => <Box display="none" {...params} />}
            component="div"
            count={filteredStopsCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={(event, newPage) => handlePageChange(newPage)}
            onRowsPerPageChange={event => setPagination({ page: INITIAL_PAGE, rowsPerPage: event.target.value })}
          />
          <Pagination
            color="primary"
            count={Math.ceil(filteredStopsCount / rowsPerPage)}
            page={page + 1}
            onChange={(event, newPage) => handlePageChange(newPage - 1)}
          />
        </Box>
      )}
    </>
  )
}

RoutingServiceList.propTypes = {
  accountId: PropTypes.string.isRequired,
  locationId: PropTypes.string,
  serviceDate: PropTypes.instanceOf(Date),
  filterByServiceName: PropTypes.string,
}

export default RoutingServiceList
